export const UI_TEXT = {
  appTitle: "Illinois Social Attitudes Aggregate Corpus (ISAAC)",
  homeTitle: "ISAAC Sampler",
  homeSubtitle:
  "Download a <strong>reproducible random sample</strong> of the labeled Reddit discourse in ISAAC, coding-free. Choose a social group and a continuous period between Jan 2007 and Dec 2023, enter how many documents you want, and click <strong>Generate Sample</strong>. The sample is drawn equally from each month in your range; set a <strong>Random Seed</strong> to make it exactly reproducible. For the definition of each column in the output, please see <a href='https://github.com/BabakHemmatian/Illinois_Social_Attitudes/blob/main/variable_list.md' style='color: red;'>here</a>.<br><br><strong>Want whole monthly files instead of a sample?</strong> Leave <strong>Number of Documents</strong> blank and click Generate — you'll get direct download links (and a Globus folder you can browse), served straight from our storage so even large pulls are fast.<br><br>For scripted queries (e.g., only comments from a specific subreddit), use the <strong>Query Playground</strong> tab; for command-line and bulk-download recipes, see the <strong>Direct Download</strong> tab.",
  socialGroupLabel: "Social Group",
  socialGroupHelper: "Select a social group to sample posts from.",
  numDocsLabel: "Number of Documents (Optional)",
  numDocsHelper: "Leave blank to get direct download links for the full monthly files (served from our Globus storage). Enter a number for a random sample drawn equally across the months you selected.",
  randomSeedLabel: "Random Seed (Optional)",
  fullFiles: {
    heading: "Whole monthly files — direct download",
    servedNote: "These files download directly from our Globus storage, not through this website, so even large pulls stay fast.",
    openInGlobus: "Browse in Globus (free Globus login)",
    downloadList: "Download file list (isaac_urls.txt)",
    linksNote: "Click any file below to download it — no account needed.",
    bulkNote: "For drag-and-drop bulk downloads without code, sign in to Globus (free; most university logins work), open the folder with the button above, and use the free Globus Connect Personal app.",
    connectAppLabel: "Get Globus Connect Personal",
    connectAppUrl: "https://www.globus.org/globus-connect-personal",
    cliNote: "Prefer the command line? The Direct Download tab has wget / aria2c recipes that use the isaac_urls.txt list above.",
    excelNote: "Tip: a few recent months are multi-gigabyte CSVs that exceed Excel's row limit — those may need a tool like DuckDB to open.",
  },
  randomSeedHelper: "If a sample size is set above, providing an integer seed produces a reproducible random subset. The seed is appended to the output filename.",
  retrieve: "Generate Sample",
  retrieving: "Generating...",
  stop: "Stop",
  currentStage: "\u23F3 Current Stage",
  downloadSample: "Download Results CSV",
  issueTitle: "Report an Issue",
  issueSubtitle:
    "If you notice any bugs/inconsistencies, or have feature suggestions, please describe them below. The email address associated with your account will be used to follow up if needed.",
  issueDescLabel: "Issue Description",
  issueSend: "Send",
  issueSending: "Sending...",
  snackbar: {
    invalidFields: "Please provide valid input fields.",
    noFiles: "No files found for the selected range.",
    fileReady: "File ready for download!",
    stopped: "Sampling stopped by user.",
    progressFailed: "Progress check failed. If the issue persists, please notify us via the \"Report and Issue\" button in the top right.",
    sampleFailed: "Error: Sampling failed. If the issue persists, please notify us via the \"Report and Issue\" button in the top right.",
    issueEmpty: "Please describe the issue.",
    issueSuccess: "Issue submitted successfully!",
    issueError: "Error: Failed to send issue report. If the issue persists, please send an email to kurdisocialcoglab@gmail.com.",
  },
  auth: {
    welcomeTitle: "Welcome to the Illinois Social Attitudes Aggregate Corpus (ISAAC)",
    welcomeText:
      "This website provides <strong>coding-free access</strong> to the Illinois Social Attitudes Aggregate Corpus, a <strong>comprehensive dataset of labeled social media discourse</strong> about six major <strong>social group distinctions</strong> (see <a href='https://github.com/BabakHemmatian/Illinois_Social_Attitudes' target='_blank' rel='noopener noreferrer'>here</a> for more information):<br><ol><li>sexuality (gay-straight)</li><li>race (Black-White)</li><li>skin tone (dark-light)</li><li>weight (thin-fat)</li><li>ability (abled-disabled)</li><li>age (young-old)</li></ol>ISAAC contains 527,060,919 thoroughly cleaned Reddit posts (64,581,610 original submissions and 462,479,309 comments). The anonymized dataset includes labels for <strong>moralized language, generalizations, sentiment, emotion and US state-level estimated user locations</strong>. See <a href='https://github.com/BabakHemmatian/Illinois_Social_Attitudes/blob/main/variable_list.md' target='_blank' rel='noopener noreferrer'>here</a> for all data columns.",
    copyright: "\u00A9 2026 Social Cognition Lab, University of Illinois at Urbana-Champaign",
    // The University of Illinois System Privacy Statement covers "all websites and
    // domains owned, controlled, operated, and/or maintained by the University of
    // Illinois System", so it already governs the email addresses collected here \u2014
    // no project-specific privacy notice is needed, only this link.
    privacyNotice:
      "<a href='https://www.vpaa.uillinois.edu/resources/web_privacy' target='_blank' rel='noopener noreferrer'>University of Illinois System Privacy Statement</a>",
    loginTitle: "Login to Your ISAAC Account",
    signupTitle: "Do not have an account with us? Create one with just an email.",
    email: "Email address",
    password: "Password",
    login: "Login",
    signup: "Sign Up",
    forgotPassword: "Forgot Password?",
    resetSent: "Reset link sent. Check your inbox.",
    resetError: "Failed to verify reset session.",
    updatePassword: "Update Password.",
    updateSuccess: "Password updated successfully.",
    passwordMismatch: "Passwords do not match.",
    resetInvalid: "Reset link is invalid or has expired.",
    agreementRequired: "You must read and accept the Data Use Agreement to create an account.",
    // ---- Data Use Agreement (scroll-through flow, 2026-08-02) ----
    // The agreement text is NOT stored here: it is fetched at runtime from
    // GET /dua, which serves the live document from the corpus repo. Only the
    // surrounding UI copy lives in this file.
    agreementTitle: "Data Use Agreement",
    agreementOpen: "Read the Data Use Agreement",
    agreementLoading: "Loading the Data Use Agreement…",
    agreementLoadError:
      "The Data Use Agreement could not be loaded. Please check your connection and try again.",
    agreementRetry: "Try again",
    agreementScrollHint: "Scroll to the end to continue",
    agreementAccept: "I have read and accept",
    agreementDecline: "Cancel",
    agreementVersionLabel: "Version {version}",
    agreementAccepted: "Data Use Agreement accepted (version {version}).",
    agreementReview: "Review again",
    // Shown when the agreement has been amended since the user accepted it.
    agreementUpdatedNotice:
      "The Data Use Agreement has been updated since you accepted it. Please review the current version and accept it to continue using ISAAC.",
    agreementSignOut: "Sign out",
    // ---- Email verification (signup) ----
    // Firebase sends a verification LINK (not a code). The link points at our
    // own /auth/action handler, which only applies the token on an explicit
    // click, so email security scanners that pre-fetch links can't burn it.
    verifyTitle: "Verify your email",
    verifyInstruction: "We emailed a verification link to <strong>{email}</strong>. Open it and click the confirm button to finish setting up your account.",
    verifySpamNote: "Don't see it? Check your spam or junk folder — the message can take a minute to arrive.",
    verifyButton: "I've verified — Continue",
    verifyResend: "Resend link",
    verifyResendCooldown: "Resend link in {seconds}s",
    verifyBack: "Use a different email",
    verifyNotYet: "Not verified yet. Open the link in your email, confirm, then press Continue.",
    verifySuccess: "Email verified! Signing you in…",
    signupCodeSent: "Account created. Check your email for a verification link.",
    loginUnverified: "This account's email isn't verified yet. Open the link we emailed you, or resend it below.",
    // NOTE: the agreement version is no longer maintained by hand here. As of
    // 2026-08-02 the backend derives it from the document itself (git commit
    // date, plus a SHA-256 of the exact bytes served) and returns it from
    // /dua; Auth.js forwards those values to /record_consent. This removes the
    // failure mode where the text changed but a hand-bumped constant did not.
  },
};
